import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FilterDef } from '@project/contracts'
import { Button, Field, Icon, Input, Modal, Notice } from '../design/index.js'
import { useApi, type SavedView } from '../app/api.js'
import { messageFor } from './feedback.js'
import { ColumnPicker, type ChoosableColumn } from './columns.js'
import { FilterFields, type FilterValues } from './filter-fields.js'

/**
 * What the Create View dialog needs to build a view in one place (INV-016,
 * SET-030): every column it may add, every filter it may set, what is on
 * screen now, and how the list spells its filters in the address bar.
 */
export interface ViewEditorConfig {
  readonly columns: readonly ChoosableColumn[]
  /** The columns on screen now, in order — where a new view starts. */
  readonly currentColumns: readonly string[]
  readonly filters: readonly FilterDef[]
  /** The filter drawer's values now, keyed as the drawer keys them. */
  readonly currentFilters: FilterValues
  readonly toParams: (values: FilterValues) => Record<string, string>
  readonly fromParams: (stored: Readonly<Record<string, string>>) => FilterValues
}

/**
 * "Create View" (docs/02 §3): a name for the way someone always looks at a
 * list.
 *
 * A view carries filter VALUES and column choices, both checked against the
 * definition by the server. Nothing here composes a query.
 */

export interface SavedViewsProps {
  readonly scope: string
  /** What would be saved if the person saved right now. */
  readonly filters: Readonly<Record<string, string>>
  readonly columns: readonly string[]
  readonly onApply: (view: SavedView) => void
  /** Applied once, when the list opens with no query of its own. */
  readonly onDefault?: (view: SavedView) => void
  /**
   * With this, views are built in the Create View dialog — name, who sees it,
   * default, columns, filters — and the current one can be edited. Without
   * it, a view saves what is on screen.
   */
  readonly editor?: ViewEditorConfig
}

/**
 * Whether what is on screen still matches the view it was opened from.
 *
 * Compared on the filters and the chosen columns, which are the only things a
 * view stores. Order does not count — the same three filters set in a different
 * sequence are the same view.
 */
const sameAsView = (
  view: SavedView,
  filters: Readonly<Record<string, string>>,
  columns: readonly string[],
): boolean => {
  const live = Object.entries(filters).filter(([, v]) => v !== '').sort()
  const saved = Object.entries(view.filters).filter(([, v]) => v !== '').sort()
  if (live.length !== saved.length) return false
  if (live.some(([k, v], i) => saved[i]?.[0] !== k || saved[i]?.[1] !== v)) return false
  // No column choice on either side is the same as matching column choices.
  if (view.columns.length === 0 && columns.length === 0) return true
  return view.columns.length === columns.length
    && [...view.columns].sort().join() === [...columns].sort().join()
}

export const SavedViews = ({ scope, filters, columns, onApply, onDefault, editor }: SavedViewsProps) => {
  const api = useApi()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  /** Which view the screen was last opened from, so edits to it can be shown. */
  const [appliedId, setAppliedId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState({ name: '', shared: false, isDefault: false })
  const [error, setError] = useState<string | null>(null)
  /** The Create / Edit View dialog: null when closed, a view when editing one. */
  const [editing, setEditing] = useState<SavedView | 'new' | null>(null)
  const appliedDefault = useRef(false)

  const views = useQuery({
    queryKey: ['views', api.companyId, scope],
    queryFn: () => api.savedViews(scope),
    enabled: scope !== '',
  })

  // The default opens the list, once, and only when nothing was asked for.
  useEffect(() => {
    if (appliedDefault.current || !onDefault) return
    const chosen = (views.data ?? []).find((v) => v.isDefault && v.mine)
    if (!chosen) return
    appliedDefault.current = true
    setAppliedId(chosen.id)
    onDefault(chosen)
  }, [views.data, onDefault])

  const save = useMutation({
    mutationFn: () =>
      api.saveView({
        scope,
        name: draft.name,
        shared: draft.shared,
        isDefault: draft.isDefault,
        filters,
        columns: [...columns],
      }),
    onSuccess: (next) => {
      client.setQueryData(['views', api.companyId, scope], next)
      setSaving(false)
      setDraft({ name: '', shared: false, isDefault: false })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (view: SavedView) => api.deleteView(view.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['views', api.companyId, scope] })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const rows = views.data ?? []
  const applied = appliedId === null ? undefined : rows.find((v) => v.id === appliedId)
  // docs/15 R-9.7 — a view with unapplied edits renders with a leading asterisk,
  // so nobody exports "All RFQs" believing it is still all of them.
  const dirty = applied !== undefined && !sameAsView(applied, filters, columns)

  return (
    <>
      <Button kind="ghost" icon={<Icon name="star" size={18} />} onClick={() => setOpen(true)}>
        {applied
          ? `${dirty ? '*' : ''}${applied.name}`
          : `Views${rows.length > 0 ? ` (${rows.length})` : ''}`}
      </Button>
      {dirty ? (
        <span className="view-dirty" title="This view has changes that have not been saved.">
          edited
        </span>
      ) : null}

      {open ? (
        <Modal
          title="Saved views"
          onClose={() => setOpen(false)}
          footerLeft={
            <>
              {editor && applied?.mine ? (
                <Button
                  kind="ghost"
                  onClick={() => {
                    setEditing(applied)
                    setOpen(false)
                  }}
                >
                  Edit current view
                </Button>
              ) : null}
              <Button
                kind="ghost"
                icon={<Icon name="plus" size={18} />}
                onClick={() => {
                  if (editor) setEditing('new')
                  else setSaving(true)
                  setOpen(false)
                }}
              >
                {editor ? 'Create new view' : 'Save what is on screen'}
              </Button>
            </>
          }
          actions={<Button kind="ghost" onClick={() => setOpen(false)}>Close</Button>}
        >
          {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}

          {rows.length === 0 ? (
            <p>
              No views yet. Set the filters and columns the way you want them, then save them here under a
              name — yours alone, or shared with everyone.
            </p>
          ) : (
            <ul className="views">
              {rows.map((view) => (
                <li className="view" key={view.id}>
                  <button
                    type="button"
                    className="view__open"
                    onClick={() => {
                      onApply(view)
                      setAppliedId(view.id)
                      setOpen(false)
                    }}
                  >
                    <span className="view__name">{view.name}</span>
                    <span className="view__meta">
                      {Object.keys(view.filters).length} filter
                      {Object.keys(view.filters).length === 1 ? '' : 's'}
                      {view.columns.length > 0 ? ` · ${view.columns.length} columns` : ''}
                      {view.shared ? ` · shared by ${view.ownerName}` : ' · yours'}
                      {view.isDefault ? ' · opens by default' : ''}
                    </span>
                  </button>
                  {view.mine ? (
                    <Button kind="ghost" disabled={remove.isPending} onClick={() => remove.mutate(view)}>
                      Delete
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      ) : null}

      {editing && editor ? (
        <ViewEditorDialog
          scope={scope}
          editor={editor}
          view={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved, all) => {
            client.setQueryData(['views', api.companyId, scope], all)
            setEditing(null)
            if (saved) {
              setAppliedId(saved.id)
              onApply(saved)
            }
          }}
        />
      ) : null}

      {saving ? (
        <Modal
          title="Save this view"
          onClose={() => setSaving(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setSaving(false)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={save.isPending || draft.name.trim().length < 2}
                onClick={() => save.mutate()}
              >
                Save it
              </Button>
            </>
          }
        >
          {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}

          <Field label="Call it" required hint="Something you will recognise: “Open orders, this month”.">
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>

          <label className="column-choice">
            <input
              type="checkbox"
              checked={draft.shared}
              onChange={(e) => setDraft({ ...draft, shared: e.currentTarget.checked })}
            />
            <span>Share it with everyone here — they can open it; only you can change it.</span>
          </label>

          <label className="column-choice">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(e) => setDraft({ ...draft, isDefault: e.currentTarget.checked })}
            />
            <span>Open this list on this view.</span>
          </label>

          <p className="muted" style={{ marginTop: 14 }}>
            It saves {Object.keys(filters).length} filter{Object.keys(filters).length === 1 ? '' : 's'}
            {columns.length > 0 ? ` and ${columns.length} chosen columns` : ' and the standard columns'}.
          </p>
        </Modal>
      ) : null}
    </>
  )
}

/**
 * "Create View" (INV-016, SET-030): name, who sees it, whether the list opens
 * on it, then Step 1 — the columns, from everything the list can show — and
 * Step 2 — the filters. One dialog, as the reference builds a view, rather
 * than "save what is on screen".
 */
const ViewEditorDialog = ({
  scope, editor, view, onClose, onSaved,
}: {
  scope: string
  editor: ViewEditorConfig
  view: SavedView | null
  onClose: () => void
  onSaved: (saved: SavedView | undefined, all: readonly SavedView[]) => void
}) => {
  const api = useApi()
  const [name, setName] = useState(view?.name ?? '')
  const [shared, setShared] = useState(view?.shared ?? false)
  const [isDefault, setIsDefault] = useState(view?.isDefault ?? false)
  const [cols, setCols] = useState<string[]>(() => [
    ...(view && view.columns.length > 0 ? view.columns : editor.currentColumns),
  ])
  const [values, setValues] = useState<FilterValues>(() =>
    view ? editor.fromParams(view.filters) : { ...editor.currentFilters })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.saveView({
      ...(view ? { id: view.id } : {}),
      scope, name: name.trim(), shared, isDefault,
      filters: editor.toParams(values),
      columns: cols,
    }),
    onSuccess: (all) => onSaved(all.find((v) => v.name === name.trim() && v.mine), all),
    onError: (err) => setError(messageFor(err)),
  })

  return (
    <Modal
      title={view ? 'Edit view' : 'Create view'}
      wide
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button
            kind="primary"
            disabled={save.isPending || name.trim().length < 2 || cols.length === 0}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}
      <Field label="View name" required>
        <Input value={name} onChange={setName} />
      </Field>
      <div role="radiogroup" aria-label="Visible to" className="row" style={{ gap: 24, marginTop: 12 }}>
        <span className="label">Visible to</span>
        <label className="row" style={{ minHeight: 44 }}>
          <input type="radio" name="view-visibility" checked={!shared} onChange={() => setShared(false)} />
          <span>Just me</span>
        </label>
        <label className="row" style={{ minHeight: 44 }}>
          <input type="radio" name="view-visibility" checked={shared} onChange={() => setShared(true)} />
          <span>All users</span>
        </label>
      </div>
      <label className="column-choice">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.currentTarget.checked)} />
        <span>Set this as default view</span>
      </label>

      <h3 className="subhead" style={{ marginTop: 20 }}>Step 1 — Columns</h3>
      <ColumnPicker all={editor.columns} value={cols} onChange={setCols} />

      <h3 className="subhead" style={{ marginTop: 20 }}>Step 2 — Filters</h3>
      <FilterFields
        filters={editor.filters}
        values={values}
        onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
      />
    </Modal>
  )
}
